/**
 * Browser persistence for the signed-in session and UI preferences.
 *
 * Tokens live in `localStorage` so a sideline tablet survives a page refresh or
 * an app restart mid-game. That is a deliberate trade-off: it keeps coaches
 * logged in through flaky connectivity, at the cost of XSS exposure, which is
 * mitigated by short access-token TTLs, refresh-token rotation and reuse
 * detection in the auth service. Every accessor is failure-tolerant — Safari
 * private mode and locked-down enterprise profiles throw on access.
 */
import { STORAGE_KEYS } from './constants';
import type { Persona } from './personas';

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** `null` for the fanatic persona (no fixed tenant). */
  tenantId: string | null;
  /** Captured at login/signup — the access token carries no slug. */
  tenantSlug: string | null;
  persona: Persona;
  /** Epoch ms at which the access token expires. */
  expiresAtMs: number;
  /** False while an MFA-required persona still owes a TOTP code. */
  isMfaSatisfied: boolean;
  isMfaEnrolled: boolean;
}

function readRaw(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch {
    // Storage unavailable: the session still works for the current page view.
  }
}

export function loadSession(): StoredSession | null {
  const raw = readRaw(STORAGE_KEYS.session);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as Partial<StoredSession>;
    if (typeof candidate.accessToken !== 'string' || typeof candidate.refreshToken !== 'string') {
      return null;
    }
    return candidate as StoredSession;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession | null): void {
  writeRaw(STORAGE_KEYS.session, session === null ? null : JSON.stringify(session));
  if (session?.tenantSlug) writeRaw(STORAGE_KEYS.lastTeamSlug, session.tenantSlug);
}

/** Last team code used on this device — prefills the login form. */
export function loadLastTeamSlug(): string {
  return readRaw(STORAGE_KEYS.lastTeamSlug) ?? '';
}

export function saveLastTeamSlug(slug: string): void {
  writeRaw(STORAGE_KEYS.lastTeamSlug, slug === '' ? null : slug);
}

export function readPreference(key: string): string | null {
  return readRaw(key);
}

/** Per-tab string value (sessionStorage): cleared when the tab closes. */
export function readSessionValue(key: string): string | null {
  try {
    return globalThis.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeSessionValue(key: string, value: string | null): void {
  try {
    if (value === null) globalThis.sessionStorage?.removeItem(key);
    else globalThis.sessionStorage?.setItem(key, value);
  } catch {
    // Non-fatal: the draft simply does not survive a reload.
  }
}

/** Per-tab flag (sessionStorage): cleared when the tab closes. */
export function readSessionFlag(key: string): boolean {
  try {
    return globalThis.sessionStorage?.getItem(key) === 'true';
  } catch {
    return false;
  }
}

export function writeSessionFlag(key: string, value: boolean): void {
  try {
    globalThis.sessionStorage?.setItem(key, String(value));
  } catch {
    // Non-fatal: the flag simply does not survive a reload.
  }
}

export function writePreference(key: string, value: string | null): void {
  writeRaw(key, value);
}
